import { Content, Header, Page } from '@backstage/core-components';
import { SearchContextProvider } from '@backstage/plugin-search-react';
import { useState } from 'react';
import {
  DevHubBox,
  DevHubButton,
  DevHubCard,
  DevHubDynamicIcon,
  DevHubGrid,
  DevHubTypography,
  darkModeTheme,
  devhubMakeStyles,
  type DevHubTheme,
} from '@devxdiscover/devhub-ui';
import DevHubSupportButton from '../common/DevHubSupportButton';
import { LearnMoreButton, OnboardIdeaButton } from './experimentation';
import { MappingExerciseFAQButton } from './MappingExerciseFAQ';
import { ConfigFeatureCheck } from '../../util/ConfigFeatureCheck';
import { Feature } from '@devxdiscover/devhub-common';
import { CreateAISystemModal } from '../ai-system/CreateAISystemModal';

const useStyles = devhubMakeStyles((theme: DevHubTheme) => ({
  hero: {
    background: `linear-gradient(135deg, ${darkModeTheme.palette.primary.main} 0%, ${darkModeTheme.palette.secondary.dark} 140%)`,
    color: theme.palette.primary.dark,
    borderRadius: 12,
    padding: theme.spacing(5, 4),
    marginBottom: theme.spacing(3),
  },
  heroContent: {
    maxWidth: 600,
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    '&::after': {
      content: '""',
      flex: 1,
      height: 1,
      backgroundColor: theme.palette.divider,
      marginLeft: theme.spacing(2),
    },
  },
  sectionIcon: {
    fontSize: '1.3rem',
    verticalAlign: 'middle',
    opacity: 0.8,
  },
  section: {
    marginBottom: theme.spacing(4),
  },
}));

/**
 * The Onboarding page component.
 * @constructor
 */
export const OnboardingPage = () => {
  const classes = useStyles();
  const [aiSystemModalOpen, setAiSystemModalOpen] = useState(false);

  return (
    <SearchContextProvider>
      <Page themeId="home">
        <Header
          title="Onboarding"
          subtitle="Discover our onboarding process and get started with DevHub."
        >
          <DevHubBox display="flex" justifyContent="flex-end">
            <DevHubSupportButton color="white">Support</DevHubSupportButton>
          </DevHubBox>
        </Header>
        <Content>
          {/* HERO BANNER */}
          <DevHubBox className={classes.hero}>
            <DevHubBox className={classes.heroContent}>
              <DevHubTypography
                variant="h4"
                style={{ fontWeight: 700, marginBottom: 8 }}
              >
                Welcome to DevHub Onboarding
              </DevHubTypography>
              <DevHubTypography
                variant="body1"
                style={{ opacity: 0.9, marginBottom: 20 }}
              >
                To get started, install the DevHub Discovery GitHub App in the
                appropriate GitHub organization. This will allow DevHub to
                discover your organization's repositories and components.
              </DevHubTypography>
              <DevHubButton
                href="https://github.com/apps/Devhub-Discovery"
                variant="contained"
                buttonColor="black"
                size="medium"
              >
                Install DevHub Discovery GitHub App
              </DevHubButton>
            </DevHubBox>
            <DevHubBox
              style={{
                marginTop: 24,
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                flexWrap: 'wrap',
                opacity: 0.85,
              }}
            >
              <DevHubDynamicIcon
                iconName="InfoOutlined"
                style={{ fontSize: '1rem', flexShrink: 0 }}
              />
              <DevHubTypography variant="body2" style={{ marginRight: 4 }}>
                Creating a business application will trigger Security and
                Privacy journeys.
              </DevHubTypography>
              <MappingExerciseFAQButton />
            </DevHubBox>
          </DevHubBox>

          {/* ── GET STARTED ── */}
          <DevHubBox className={classes.section}>
            <DevHubBox className={classes.sectionTitle}>
              <DevHubDynamicIcon
                iconName="CheckCircleOutline"
                className={classes.sectionIcon}
                color="primary"
              />
              <DevHubTypography variant="subtitle1" style={{ fontWeight: 600 }}>
                Get Started
              </DevHubTypography>
            </DevHubBox>

            <DevHubGrid container spacing={3}>
              <DevHubGrid item xs={12} sm={12} md={6}>
                <DevHubCard
                  density="tiny"
                  consistentHeight
                  topBorder="custom"
                  topBorderColor="blue"
                  title="Onboard a Business Application"
                  subTitle="Our new onboarding tool which replaces the ITRC onboarding process. Provision your business application in minutes."
                  bodyPosition="bottom"
                  body={
                    <DevHubGrid container spacing={1}>
                      <DevHubGrid item>
                        <DevHubButton
                          href="/onboarding/create"
                          buttonColor="black"
                          variant="contained"
                          size="medium"
                        >
                          Onboard an Application
                        </DevHubButton>
                      </DevHubGrid>
                    </DevHubGrid>
                  }
                />
              </DevHubGrid>
              <ConfigFeatureCheck feature={Feature.AI_INVENTORY}>
                <DevHubGrid item xs={12} sm={12} md={6}>
                  <DevHubCard
                    density="tiny"
                    consistentHeight
                    topBorder="custom"
                    topBorderColor="blue"
                    title="Onboard an AI System"
                    subTitle="Lightweight onboarding for AI Systems linked to approved use cases. No infrastructure provisioning."
                    bodyPosition="bottom"
                    body={
                      <DevHubGrid container spacing={1}>
                        <DevHubGrid item>
                          <DevHubButton
                            onClick={() => setAiSystemModalOpen(true)}
                            buttonColor="black"
                            variant="contained"
                            size="medium"
                          >
                            Onboard an AI System
                          </DevHubButton>
                        </DevHubGrid>
                      </DevHubGrid>
                    }
                  />
                </DevHubGrid>
              </ConfigFeatureCheck>
              <DevHubGrid item xs={12} sm={12} md={6}>
                <DevHubCard
                  density="tiny"
                  consistentHeight
                  topBorder="custom"
                  topBorderColor="blue"
                  title="Experimental Application"
                  subTitle="Quick onboarding for time-boxed Lab Week projects, prototypes, and short-term trials. Get GitHub, CNAP, and Active Directory auto-provisioned."
                  bodyPosition="bottom"
                  body={
                    <DevHubGrid container spacing={1}>
                      <DevHubGrid item>
                        <LearnMoreButton
                          variant="contained"
                          buttonColor="black"
                          size="medium"
                        />
                      </DevHubGrid>
                      <DevHubGrid item>
                        <OnboardIdeaButton size="medium" />
                      </DevHubGrid>
                    </DevHubGrid>
                  }
                />
              </DevHubGrid>
              <DevHubGrid item xs={12} sm={12} md={6}>
                <DevHubCard
                  density="tiny"
                  consistentHeight
                  topBorder="custom"
                  topBorderColor="blue"
                  title="Documentation"
                  subTitle="DevHub offers a centralized place for documentation. Learn more about how to add, update, and manage your documentation."
                  bodyPosition="bottom"
                  body={
                    <DevHubGrid container spacing={1}>
                      <DevHubGrid item>
                        <DevHubButton
                          href="/documentation"
                          buttonColor="black"
                          variant="contained"
                          size="medium"
                        >
                          Documentation Guide
                        </DevHubButton>
                      </DevHubGrid>
                    </DevHubGrid>
                  }
                />
              </DevHubGrid>
            </DevHubGrid>
          </DevHubBox>

          {/* ── EXTEND DEVHUB ── */}
          <DevHubBox className={classes.section}>
            <DevHubBox className={classes.sectionTitle}>
              <DevHubDynamicIcon
                iconName="Extension"
                className={classes.sectionIcon}
                color="secondary"
              />
              <DevHubTypography variant="subtitle1" style={{ fontWeight: 600 }}>
                Extend DevHub
              </DevHubTypography>
            </DevHubBox>

            <DevHubGrid container spacing={3}>
              <DevHubGrid item xs={12} sm={12} lg={8}>
                <DevHubCard
                  density="cozy"
                  consistentHeight
                  topBorder="custom"
                  topBorderColor="secondary"
                  title="DevHub Extensibility Program"
                  subTitle="DevHub is a centralized hub of information for developers at Comcast. The DevHub team has provided the ability to extend the platform to outside collaborators! Get started by filling out the Extension Intake Form."
                  bodyPosition="bottom"
                  body={
                    <DevHubGrid container spacing={1}>
                      <DevHubGrid item>
                        <DevHubButton
                          buttonColor="black"
                          href="/docs/default/component/devhub-extensibility/"
                          variant="contained"
                          size="medium"
                        >
                          Learn More
                        </DevHubButton>
                      </DevHubGrid>
                      <DevHubGrid item>
                        <DevHubButton
                          buttonColor="black"
                          href="https://forms.office.com/pages/responsepage.aspx?id=6e9qkKd2ZU-4LV7CB3XVqs-kGWJxwmJBhi6sKSuH1BZUM01QUTRZRUw0WkJTSDM5TE4zUk1TSFNBRS4u"
                          variant="contained"
                          size="medium"
                        >
                          Extension Intake Form
                        </DevHubButton>
                      </DevHubGrid>
                    </DevHubGrid>
                  }
                />
              </DevHubGrid>
              <DevHubGrid item xs={12} sm={12} lg={4}>
                <DevHubCard
                  density="cozy"
                  consistentHeight
                  topBorder="custom"
                  topBorderColor="secondary"
                  title="DevHub Lite"
                  subTitle="A lightweight local version of DevHub for testing plugins, templates, and other code changes."
                  bodyPosition="bottom"
                  body={
                    <DevHubButton
                      href="/docs/default/component/devhub-extensibility/devhub-lite/"
                      buttonColor="black"
                      variant="contained"
                      size="medium"
                    >
                      DevHub Lite Guide
                    </DevHubButton>
                  }
                />
              </DevHubGrid>
            </DevHubGrid>
          </DevHubBox>

          {/* ── DEVELOPER RESOURCES ── */}
          <DevHubBox className={classes.section}>
            <DevHubBox className={classes.sectionTitle}>
              <DevHubDynamicIcon
                iconName="LibraryBooks"
                className={classes.sectionIcon}
                color="primary"
              />
              <DevHubTypography variant="subtitle1" style={{ fontWeight: 600 }}>
                Developer Resources
              </DevHubTypography>
            </DevHubBox>

            <DevHubGrid container spacing={3}>
              <DevHubGrid item xs={12} sm={12} lg={6}>
                <DevHubCard
                  density="cozy"
                  consistentHeight
                  topBorder="info"
                  title="Plugins"
                  subTitle="Plugins extend the functionality of DevHub. Learn about configuring, publishing, and managing your plugins."
                  bodyPosition="bottom"
                  body={
                    <DevHubButton
                      href="/docs/default/component/devhub-extensibility/frontend-plugin-development/"
                      buttonColor="black"
                      variant="contained"
                      size="medium"
                    >
                      Plugin Development
                    </DevHubButton>
                  }
                />
              </DevHubGrid>
              <DevHubGrid item xs={12} sm={12} lg={6}>
                <DevHubCard
                  density="cozy"
                  consistentHeight
                  topBorder="info"
                  title="Templates"
                  subTitle="Templates are critical for creating and modifying services. Learn about configuring, publishing, and managing your templates."
                  bodyPosition="bottom"
                  body={
                    <DevHubButton
                      href="/docs/default/component/devhub-template-docs"
                      buttonColor="black"
                      variant="contained"
                      size="medium"
                    >
                      Template Development
                    </DevHubButton>
                  }
                />
              </DevHubGrid>
            </DevHubGrid>
          </DevHubBox>
        </Content>
      </Page>
      {aiSystemModalOpen && (
        <CreateAISystemModal
          open={aiSystemModalOpen}
          onClose={() => setAiSystemModalOpen(false)}
        />
      )}
    </SearchContextProvider>
  );
};
